"use client";

import { useTranslations } from "next-intl";
import {
  motion,
  useTransform,
  useInView,
  useMotionValue,
  animate,
} from "framer-motion";
import { useEffect, useRef } from "react";
import { Section } from "@/components/ui/Section";
import { CheckCircle, Smile, Clock, Code } from "lucide-react";

// Stats Data - simplified structure for cleaner UI
const stats = [
  {
    key: "projects",
    value: "22+",
    icon: CheckCircle,
    color: "text-[#00b300]",
  },
  { key: "customers", value: "32+", icon: Smile, color: "text-[#ffbf00]" },
  { key: "hours", value: "2560+", icon: Clock, color: "text-[#00a3e0]" },
  { key: "lines", value: "18M+", icon: Code, color: "text-[#ff0000]" },
];

function Counter({ value }: { value: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => {
    // Large numbers need clean formatting
    return Math.floor(latest).toLocaleString();
  });

  const numericValue = parseFloat(value.replace(/[^0-9.]/g, ""));
  const suffix = value.replace(/[0-9.]/g, "");

  useEffect(() => {
    if (inView) {
      // Premium count-up with smooth deceleration
      const controls = animate(count, numericValue, {
        duration: 1.5,
        ease: [0.22, 1, 0.36, 1], // Custom "OutExpo/Quint" hybrid for solid feel
      });
      return controls.stop;
    }
  }, [inView, numericValue, count]);

  return (
    <motion.span
      ref={ref}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="inline-block"
    >
      <motion.span>{rounded}</motion.span>
      {suffix}
    </motion.span>
  );
}

export function Stats() {
  const t = useTranslations("Index");
  const tStats = useTranslations("Stats");

  return (
    <Section
      id="stats"
      title={t("stats_title")}
      subtitle={t("stats_subtitle")}
      className="py-24 bg-(--bg-card) border-t border-(--border-subtle)"
      contentClassName="px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.1 },
            },
          }}
          className="group relative grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-(--border-subtle) bg-(--bg-primary)/50 rounded-3xl border border-(--border-subtle) backdrop-blur-sm overflow-hidden"
        >
          {/* Ambient Light – orbiting inside the card */}
          <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
            <motion.div
              animate={{
                // very slow, irregular circular-ish path
                x: [
                  "-40%",
                  "40%",
                  "40%",
                  "-40%",
                  "-40%",
                  "10%",
                  "-10%",
                  "-40%",
                ],
                y: [
                  "-40%",
                  "-40%",
                  "40%",
                  "40%",
                  "-40%",
                  "-10%",
                  "10%",
                  "-40%",
                ],
                opacity: [
                  "calc(0.05 + var(--ambient-hover))",
                  "calc(0.12 + var(--ambient-hover))",
                  "calc(0.08 + var(--ambient-hover))",
                  "calc(0.1 + var(--ambient-hover))",
                  "calc(0.06 + var(--ambient-hover))",
                  "calc(0.1 + var(--ambient-hover))",
                  "calc(0.07 + var(--ambient-hover))",
                  "calc(0.05 + var(--ambient-hover))",
                ],
              }}
              transition={{
                x: {
                  duration: 60,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
                y: {
                  duration: 60,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
                opacity: {
                  duration: 60,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
              // Fixed: Using CSS transition for interactions to avoid Framer variant/animate conflicts
              // group-hover:brightness-[1.02] adds subtle +2% brightness when card is hovered
              className="
  absolute
  top-1/2 left-1/2
  -translate-x-1/2 -translate-y-1/2
  w-[90%] h-[90%]
  rounded-full
  blur-[120px]
  filter
  transition-[filter] duration-500
  [--ambient-hover:0]
  group-hover:[--ambient-hover:0.015]
"
              style={{
                background:
                  "radial-gradient(circle at center, var(--brand-primary) 0%, transparent 65%)",
              }}
            />
          </div>

          {stats.map(({ key, value, icon: Icon, color }) => (
            <motion.div
              key={key}
              variants={{
                hidden: { opacity: 0, y: 10 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.5, ease: "easeOut" },
                },
              }}
              className="relative z-10 flex flex-col items-center justify-center text-center p-8 lg:p-12"
            >
              {/* Icon - Static & Clean */}
              <div className="mb-6 p-4 rounded-2xl bg-(--bg-card) border border-(--border-subtle)">
                <Icon size={32} className={color} strokeWidth={1.5} />
              </div>

              {/* Number - Massive & Sharp */}
              <div className="mb-4">
                <span className="text-5xl lg:text-6xl font-bold tracking-tight text-(--text-primary)">
                  <Counter value={value} />
                </span>
              </div>

              {/* Separator - Static Colored Line */}
              <div
                className={`h-1 w-8 rounded-full bg-current opacity-50 mb-4 ${color}`}
              />

              {/* Label - Minimal */}
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-(--text-secondary)">
                {tStats(key)}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
}
